import React, { useState } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Slider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  LinearProgress,
  Tooltip,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Build as BuildIcon,
  Person as PersonIcon,
  Schedule as ScheduleIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Speed as SpeedIcon,
} from '@mui/icons-material';
import { DashboardLayout } from '../../components/layout/DashboardLayout';

interface ServiceBay {
  id: string;
  name: string;
  type: 'general' | 'diagnostic' | 'bodywork' | 'electrical' | 'mechanical';
  status: 'available' | 'occupied' | 'maintenance' | 'reserved';
  technician: string | null;
  currentJob: string | null;
  capacityPercentage: number;
  nextAvailable: Date | null;
}

interface Technician {
  id: string;
  name: string;
  specialization: 'general' | 'diagnostic' | 'bodywork' | 'electrical' | 'mechanical';
  status: 'available' | 'busy' | 'off-duty';
  currentBay: string | null;
  efficiency: number;
  workload: number;
}

export const CapacityManagement: React.FC = () => {
  const [serviceBays, setServiceBays] = useState<ServiceBay[]>([
    {
      id: '1',
      name: 'Bay 1',
      type: 'general',
      status: 'available',
      technician: null,
      currentJob: null,
      capacityPercentage: 0,
      nextAvailable: null,
    },
    {
      id: '2',
      name: 'Bay 2',
      type: 'diagnostic',
      status: 'occupied',
      technician: '1',
      currentJob: 'Engine Diagnostics - Toyota Corolla',
      capacityPercentage: 75,
      nextAvailable: new Date(2023, 2, 15, 14, 30),
    },
    {
      id: '3',
      name: 'Bay 3',
      type: 'mechanical',
      status: 'maintenance',
      technician: null,
      currentJob: 'Bay Maintenance',
      capacityPercentage: 100,
      nextAvailable: new Date(2023, 2, 16, 9, 0),
    },
    {
      id: '4',
      name: 'Bay 4',
      type: 'bodywork',
      status: 'reserved',
      technician: '3',
      currentJob: 'Reserved for BMW X5 Collision Repair',
      capacityPercentage: 0,
      nextAvailable: new Date(2023, 2, 15, 11, 0),
    },
  ]);

  const [technicians, setTechnicians] = useState<Technician[]>([
    {
      id: '1',
      name: 'John Smith',
      specialization: 'diagnostic',
      status: 'busy',
      currentBay: '2',
      efficiency: 85,
      workload: 75,
    },
    {
      id: '2',
      name: 'Sarah Johnson',
      specialization: 'electrical',
      status: 'available',
      currentBay: null,
      efficiency: 90,
      workload: 30,
    },
    {
      id: '3',
      name: 'Mike Williams',
      specialization: 'bodywork',
      status: 'off-duty',
      currentBay: null,
      efficiency: 78,
      workload: 0,
    },
    {
      id: '4',
      name: 'David Brown',
      specialization: 'mechanical',
      status: 'available',
      currentBay: null,
      efficiency: 92,
      workload: 45,
    },
  ]);

  const [openBayDialog, setOpenBayDialog] = useState(false);
  const [openTechnicianDialog, setOpenTechnicianDialog] = useState(false);
  const [selectedBay, setSelectedBay] = useState<ServiceBay | null>(null);
  const [selectedTechnician, setSelectedTechnician] = useState<Technician | null>(null);

  // Calculate overall capacity
  const calculateOverallCapacity = () => {
    const totalBays = serviceBays.length;
    const availableBays = serviceBays.filter(bay => bay.status === 'available').length;
    const occupiedBays = serviceBays.filter(bay => bay.status === 'occupied').length;
    const maintenanceBays = serviceBays.filter(bay => bay.status === 'maintenance').length;
    const reservedBays = serviceBays.filter(bay => bay.status === 'reserved').length;

    const capacityPercentage = (occupiedBays + reservedBays) / totalBays * 100;

    return {
      totalBays,
      availableBays,
      occupiedBays,
      maintenanceBays,
      reservedBays,
      capacityPercentage,
    };
  };

  const capacity = calculateOverallCapacity();

  const handleOpenBayDialog = (bay: ServiceBay | null) => {
    setSelectedBay(bay);
    setOpenBayDialog(true);
  };

  const handleCloseBayDialog = () => {
    setOpenBayDialog(false);
    setSelectedBay(null);
  };

  const handleOpenTechnicianDialog = (technician: Technician | null) => {
    setSelectedTechnician(technician);
    setOpenTechnicianDialog(true);
  };

  const handleCloseTechnicianDialog = () => {
    setOpenTechnicianDialog(false);
    setSelectedTechnician(null);
  };

  const handleSaveBay = () => {
    // Implementation for saving bay changes
    handleCloseBayDialog();
  };

  const handleSaveTechnician = () => {
    // Implementation for saving technician changes
    handleCloseTechnicianDialog();
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'available':
        return 'success';
      case 'occupied':
        return 'primary';
      case 'maintenance':
        return 'warning';
      case 'reserved':
        return 'info';
      case 'busy':
        return 'primary';
      case 'off-duty':
        return 'error';
      default:
        return 'default';
    }
  };

  const formatDate = (date: Date | null) => {
    if (!date) return 'N/A';
    return date.toLocaleString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <DashboardLayout>
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          Capacity Management
        </Typography>

        {/* Capacity Overview */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Overall Capacity
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Box sx={{ width: '100%', mr: 1 }}>
                    <LinearProgress
                      variant="determinate"
                      value={capacity.capacityPercentage}
                      color={
                        capacity.capacityPercentage < 50
                          ? 'success'
                          : capacity.capacityPercentage < 80
                          ? 'warning'
                          : 'error'
                      }
                      sx={{ height: 10, borderRadius: 5 }}
                    />
                  </Box>
                  <Box sx={{ minWidth: 35 }}>
                    <Typography variant="body2" color="text.secondary">
                      {`${Math.round(capacity.capacityPercentage)}%`}
                    </Typography>
                  </Box>
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={6} md={3}>
                    <Paper sx={{ p: 1, textAlign: 'center' }}>
                      <Typography variant="body2" color="text.secondary">
                        Total
                      </Typography>
                      <Typography variant="h6">{capacity.totalBays}</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Paper sx={{ p: 1, textAlign: 'center', bgcolor: 'success.light' }}>
                      <Typography variant="body2" color="text.secondary">
                        Available
                      </Typography>
                      <Typography variant="h6">{capacity.availableBays}</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Paper sx={{ p: 1, textAlign: 'center', bgcolor: 'primary.light' }}>
                      <Typography variant="body2" color="text.secondary">
                        Occupied
                      </Typography>
                      <Typography variant="h6">{capacity.occupiedBays}</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Paper sx={{ p: 1, textAlign: 'center', bgcolor: 'warning.light' }}>
                      <Typography variant="body2" color="text.secondary">
                        Maintenance
                      </Typography>
                      <Typography variant="h6">{capacity.maintenanceBays}</Typography>
                    </Paper>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Technician Availability
                </Typography>
                <Box sx={{ mb: 2 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={4}>
                      <Paper sx={{ p: 1, textAlign: 'center', bgcolor: 'success.light' }}>
                        <Typography variant="body2" color="text.secondary">
                          Available
                        </Typography>
                        <Typography variant="h6">
                          {technicians.filter(tech => tech.status === 'available').length}
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={4}>
                      <Paper sx={{ p: 1, textAlign: 'center', bgcolor: 'primary.light' }}>
                        <Typography variant="body2" color="text.secondary">
                          Busy
                        </Typography>
                        <Typography variant="h6">
                          {technicians.filter(tech => tech.status === 'busy').length}
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={4}>
                      <Paper sx={{ p: 1, textAlign: 'center', bgcolor: 'error.light' }}>
                        <Typography variant="body2" color="text.secondary">
                          Off-Duty
                        </Typography>
                        <Typography variant="h6">
                          {technicians.filter(tech => tech.status === 'off-duty').length}
                        </Typography>
                      </Paper>
                    </Grid>
                  </Grid>
                </Box>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => handleOpenTechnicianDialog(null)}
                  fullWidth
                >
                  Add New Technician
                </Button>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Service Bays */}
        <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
          Service Bays
        </Typography>
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenBayDialog(null)}
          >
            Add New Bay
          </Button>
        </Box>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Bay Name</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Technician</TableCell>
                <TableCell>Current Job</TableCell>
                <TableCell>Capacity</TableCell>
                <TableCell>Next Available</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {serviceBays.map(bay => (
                <TableRow key={bay.id}>
                  <TableCell>{bay.name}</TableCell>
                  <TableCell>
                    <Chip
                      label={bay.type.charAt(0).toUpperCase() + bay.type.slice(1)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={bay.status.charAt(0).toUpperCase() + bay.status.slice(1)}
                      color={getStatusColor(bay.status) as any}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {bay.technician
                      ? technicians.find(tech => tech.id === bay.technician)?.name || 'Unknown'
                      : 'None'}
                  </TableCell>
                  <TableCell>{bay.currentJob || 'None'}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Box sx={{ width: '100%', mr: 1 }}>
                        <LinearProgress
                          variant="determinate"
                          value={bay.capacityPercentage}
                          color={
                            bay.capacityPercentage < 50
                              ? 'success'
                              : bay.capacityPercentage < 80
                              ? 'warning'
                              : 'error'
                          }
                        />
                      </Box>
                      <Box sx={{ minWidth: 35 }}>
                        <Typography variant="body2" color="text.secondary">
                          {`${bay.capacityPercentage}%`}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>{formatDate(bay.nextAvailable)}</TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={() => handleOpenBayDialog(bay)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" color="error">
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Technicians */}
        <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
          Technicians
        </Typography>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Specialization</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Current Bay</TableCell>
                <TableCell>Efficiency</TableCell>
                <TableCell>Workload</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {technicians.map(technician => (
                <TableRow key={technician.id}>
                  <TableCell>{technician.name}</TableCell>
                  <TableCell>
                    <Chip
                      label={
                        technician.specialization.charAt(0).toUpperCase() +
                        technician.specialization.slice(1)
                      }
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={technician.status.charAt(0).toUpperCase() + technician.status.slice(1)}
                      color={getStatusColor(technician.status) as any}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {technician.currentBay
                      ? serviceBays.find(bay => bay.id === technician.currentBay)?.name || 'Unknown'
                      : 'None'}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Box sx={{ width: '100%', mr: 1 }}>
                        <LinearProgress
                          variant="determinate"
                          value={technician.efficiency}
                          color={
                            technician.efficiency < 50
                              ? 'error'
                              : technician.efficiency < 75
                              ? 'warning'
                              : 'success'
                          }
                        />
                      </Box>
                      <Box sx={{ minWidth: 35 }}>
                        <Typography variant="body2" color="text.secondary">
                          {`${technician.efficiency}%`}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Box sx={{ width: '100%', mr: 1 }}>
                        <LinearProgress
                          variant="determinate"
                          value={technician.workload}
                          color={
                            technician.workload < 50
                              ? 'success'
                              : technician.workload < 80
                              ? 'warning'
                              : 'error'
                          }
                        />
                      </Box>
                      <Box sx={{ minWidth: 35 }}>
                        <Typography variant="body2" color="text.secondary">
                          {`${technician.workload}%`}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() => handleOpenTechnicianDialog(technician)}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" color="error">
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Service Bay Dialog */}
        <Dialog open={openBayDialog} onClose={handleCloseBayDialog} maxWidth="sm" fullWidth>
          <DialogTitle>{selectedBay ? 'Edit Service Bay' : 'Add New Service Bay'}</DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Bay Name"
                    defaultValue={selectedBay?.name || ''}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Bay Type</InputLabel>
                    <Select
                      label="Bay Type"
                      defaultValue={selectedBay?.type || 'general'}
                    >
                      <MenuItem value="general">General</MenuItem>
                      <MenuItem value="diagnostic">Diagnostic</MenuItem>
                      <MenuItem value="bodywork">Bodywork</MenuItem>
                      <MenuItem value="electrical">Electrical</MenuItem>
                      <MenuItem value="mechanical">Mechanical</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select
                      label="Status"
                      defaultValue={selectedBay?.status || 'available'}
                    >
                      <MenuItem value="available">Available</MenuItem>
                      <MenuItem value="occupied">Occupied</MenuItem>
                      <MenuItem value="maintenance">Maintenance</MenuItem>
                      <MenuItem value="reserved">Reserved</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Assigned Technician</InputLabel>
                    <Select
                      label="Assigned Technician"
                      defaultValue={selectedBay?.technician || ''}
                    >
                      <MenuItem value="">None</MenuItem>
                      {technicians.map(tech => (
                        <MenuItem key={tech.id} value={tech.id}>
                          {tech.name} ({tech.specialization})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Current Job"
                    defaultValue={selectedBay?.currentJob || ''}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography gutterBottom>Capacity Percentage</Typography>
                  <Slider
                    defaultValue={selectedBay?.capacityPercentage || 0}
                    valueLabelDisplay="auto"
                    step={5}
                    marks
                    min={0}
                    max={100}
                  />
                </Grid>
              </Grid>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseBayDialog}>Cancel</Button>
            <Button onClick={handleSaveBay} variant="contained">
              Save
            </Button>
          </DialogActions>
        </Dialog>

        {/* Technician Dialog */}
        <Dialog
          open={openTechnicianDialog}
          onClose={handleCloseTechnicianDialog}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            {selectedTechnician ? 'Edit Technician' : 'Add New Technician'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Technician Name"
                    defaultValue={selectedTechnician?.name || ''}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Specialization</InputLabel>
                    <Select
                      label="Specialization"
                      defaultValue={selectedTechnician?.specialization || 'general'}
                    >
                      <MenuItem value="general">General</MenuItem>
                      <MenuItem value="diagnostic">Diagnostic</MenuItem>
                      <MenuItem value="bodywork">Bodywork</MenuItem>
                      <MenuItem value="electrical">Electrical</MenuItem>
                      <MenuItem value="mechanical">Mechanical</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select
                      label="Status"
                      defaultValue={selectedTechnician?.status || 'available'}
                    >
                      <MenuItem value="available">Available</MenuItem>
                      <MenuItem value="busy">Busy</MenuItem>
                      <MenuItem value="off-duty">Off-Duty</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Assigned Bay</InputLabel>
                    <Select
                      label="Assigned Bay"
                      defaultValue={selectedTechnician?.currentBay || ''}
                    >
                      <MenuItem value="">None</MenuItem>
                      {serviceBays.map(bay => (
                        <MenuItem key={bay.id} value={bay.id}>
                          {bay.name} ({bay.type})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <Typography gutterBottom>Efficiency Rating</Typography>
                  <Slider
                    defaultValue={selectedTechnician?.efficiency || 75}
                    valueLabelDisplay="auto"
                    step={5}
                    marks
                    min={0}
                    max={100}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography gutterBottom>Current Workload</Typography>
                  <Slider
                    defaultValue={selectedTechnician?.workload || 0}
                    valueLabelDisplay="auto"
                    step={5}
                    marks
                    min={0}
                    max={100}
                  />
                </Grid>
              </Grid>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseTechnicianDialog}>Cancel</Button>
            <Button onClick={handleSaveTechnician} variant="contained">
              Save
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </DashboardLayout>
  );
}; 